import { useState, useMemo } from 'react';
import { SyncedGoogleAdminAuditEntry } from '../../types';
import { FileText, Search, AlertTriangle } from 'lucide-react';

interface GoogleAdminAuditProps {
  events: SyncedGoogleAdminAuditEntry[];
}

// Sensitive action keywords to color-code in amber
const SENSITIVE_ACTIONS = [
  'password', 'reset', 'role', 'admin', 'create user', 'delete user',
  'suspend user', 'unsuspend', '2sv', 'mfa', 'recovery', 'privilege',
];

function isSensitiveAction(event: SyncedGoogleAdminAuditEntry): boolean {
  if (event.isSensitive) return true;
  const actionLower = event.action.toLowerCase();
  return SENSITIVE_ACTIONS.some(s => actionLower.includes(s));
}

export default function GoogleAdminAudit({ events }: GoogleAdminAuditProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSensitiveOnly, setShowSensitiveOnly] = useState(false);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [events]);

  const filteredEvents = useMemo(() => {
    let result = sortedEvents;
    if (showSensitiveOnly) {
      result = result.filter(e => isSensitiveAction(e));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e =>
        e.adminName.toLowerCase().includes(q) ||
        e.adminEmail.toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q) ||
        e.target.toLowerCase().includes(q) ||
        e.details.toLowerCase().includes(q)
      );
    }
    return result;
  }, [sortedEvents, showSensitiveOnly, searchQuery]);

  const sensitiveCount = useMemo(() => {
    return sortedEvents.filter(e => isSensitiveAction(e)).length;
  }, [sortedEvents]);

  if (events.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">
          <FileText className="h-5 w-5 inline mr-2" />
          Admin Audit Log
        </h3>
        <p className="text-muted-foreground text-sm">No admin audit data available. Trigger a sync to collect Google Workspace data.</p>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-foreground mb-4">
        <FileText className="h-5 w-5 inline mr-2" />
        Admin Audit Log
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          {events.length} events
          {sensitiveCount > 0 && (
            <span className="ml-2 text-amber-600 dark:text-amber-400">
              ({sensitiveCount} sensitive)
            </span>
          )}
        </span>
      </h3>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search admins, actions, targets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <button
          onClick={() => setShowSensitiveOnly(!showSensitiveOnly)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
            showSensitiveOnly
              ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <AlertTriangle className="h-4 w-4" />
          Sensitive Only
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Date</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Admin</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Action</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Target</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((event, idx) => {
              const sensitive = isSensitiveAction(event);
              return (
                <tr
                  key={`${event.id}-${idx}`}
                  className={`border-b border-border/50 hover:bg-muted/30 ${
                    sensitive ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
                  }`}
                >
                  <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(event.timestamp).toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    <div className="font-medium text-foreground">{event.adminName}</div>
                    <div className="text-xs">{event.adminEmail}</div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`font-medium ${
                      sensitive
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-foreground'
                    }`}>
                      {event.action}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-foreground">{event.target || '-'}</td>
                  <td className="py-2 px-3 text-muted-foreground text-xs max-w-xs truncate" title={event.details}>
                    {event.details || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredEvents.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">No events match your filters.</div>
        )}
      </div>
    </div>
  );
}
